// src/automation/components/AutomationAdmin.js
//
// Admin panel for the Automation console — same shell/look as the WBR
// AdminPageV2 (embedded header + tab bar), reusing the WBR admin CSS. For now
// it has one tab: Access. The tab array is ready for more (e.g. global config)
// later. Gated on canAdmin by the caller (and defensively here).

import React, { useState } from "react";
import { Settings, Shield, Lock, SlidersHorizontal, KeyRound } from "lucide-react";
import { TabButton } from "wbr/components/admin/AdminShared";
import AutomationAccessTab from "./AutomationAccessTab";
import AutomationConfigTab from "./AutomationConfigTab";
import AutomationCookiesTab from "./AutomationCookiesTab";
import "wbr/components/admin/AdminPage.css";

const TABS = [
  { id: "access", label: "Access", icon: Shield },
  { id: "automations", label: "Automations", icon: SlidersHorizontal },
  // Auth (cookies, tokens, global config) is owner-only.
  { id: "auth", label: "Auth", icon: KeyRound, ownerOnly: true },
];

export default function AutomationAdmin({ sections = [], canAdmin, canManageUsers, role, isOwner = false, perms = {} }) {
  const [activeTab, setActiveTab] = useState("access");
  const tabs = TABS.filter((t) => !t.ownerOnly || isOwner);

  if (!canAdmin) {
    return (
      <div className="admin-panel-embedded">
        <div className="admin-access-denied">
          <Lock size={48} />
          <h2>Admin Access Required</h2>
          <p>
            Your role is <strong>{role || "no access"}</strong>. Only contributors
            and admins can open this panel.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel-embedded">
      <div className="admin-embedded-header">
        <div className="admin-embedded-header__left">
          <Settings size={20} />
          <div>
            <h2 className="admin-embedded-header__title">Automation Admin</h2>
            <span className="admin-embedded-header__subtitle">
              Manage access &amp; configuration
            </span>
          </div>
        </div>
      </div>

      <div className="admin-body">
        <div className="admin-tabs">
          {tabs.map((tab) => (
            <TabButton
              key={tab.id}
              icon={tab.icon}
              label={tab.label}
              active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            />
          ))}
        </div>

        <div className="admin-tab-panel">
          {activeTab === "access" && (
            <AutomationAccessTab
              sections={sections}
              canManageUsers={canManageUsers}
              isAdmin={!!perms.isAdmin}
              scopeTasks={perms.isAdmin ? null : perms.allowedTasks || []}
            />
          )}
          {activeTab === "automations" && <AutomationConfigTab perms={perms} />}
          {activeTab === "auth" && isOwner && <AutomationCookiesTab />}
        </div>
      </div>
    </div>
  );
}
